import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Popup, SemanticICONS } from 'semantic-ui-react'
import { DownloadService } from '~/api/services/downloadFiles'
import CustomIcon from '~/components/CustomIcon'
import i18n from '~/components/i18n'
import Layout from '~/components/Layout'
import TableComponent from '~/components/TableComponent'
import { FarmersQuery } from '~/QueryHooks/Farmers'
import { handleErrors } from '~/utils/helpers'
import PreviewModal from './PreviewModal'

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('ID'),
    key: 'uniqueId'
  },
  {
    name: i18n.t('Period under consideration'),
    key: 'period',
    type: 'climate-check-period'
  },
  {
    name: i18n.t('Title/Topic'),
    key: 'title'
  },
  {
    name: i18n.t('Start/end data collection'),
    key: 'start',
    type: 'startEndSurvey'
  },
  {
    name: i18n.t('Completed at'),
    key: 'completedAt',
    type: 'date',
    format: 'dddd, D MMMM YYYY HH:mm'
  },
  {
    name: i18n.t('Emission factor (kg CO2e / kg FPCM)'),
    key: 'soldMilkEconomicAllocationFpcmIdf',
    type: 'number-or-value',
    unit: 'kg CO2e / kg FPCM',
    decimalScale: 2
  },
  {
    name: i18n.t('Actions')
  }
];

const ClimaFootprint: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()

  const [uniqueId, setUniqueId] = useState(null)
  const [previewModalId, setPreviewModalId] = useState(null)
  const [dataFiltered, setDataFiltered] = useState({
    rows: 20,
    page: 0
  })
  const [loadingAction, setLoadingAction] = useState({
    type: '',
    id: null,
    value: false,
    idKey: ''
  })

  const actions = [
    {
      popupMessage: i18n.t('Show'),
      name: 'eye',
      type: 'icon',
      renderActions: getActions,
      actionType: 'show'
    },
    {
      popupMessage: <span dangerouslySetInnerHTML={{ __html: t('CO2 emission result') }} />,
      type: 'icon',
      name: 'file pdf',
      size: 'large',
      actionType: 'pdf-results',
      renderActions: getActions
    }
  ]

  const tableColumnHeaders = getDefaultTableColumnHeaders()

  const {
    data: co2Footprints,
    isLoading: isLoading,
    isFetching: isFetching,
    refetch: fetchData
  } = FarmersQuery.getClimaFootprints(id, dataFiltered)

  useEffect(() => {
    fetchData()
  }, [dataFiltered])

  async function handleDownloadPdfResults(row) {
    setLoadingAction({
      type: 'pdf-results',
      id: row.id,
      value: true,
      idKey: 'id'
    })
    try {
      await DownloadService.downloadFileWithName(`farmers/${id}/climate-check-footprint/results/${row.id}/pdf`)
    } catch (error) {
      handleErrors(error)
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  function handleShow(row) {
    setPreviewModalId(row?.id)
    setUniqueId(row?.uniqueId)
  }

  function getActions(row, actionType) {
    const action = actions.find(action => action.actionType === actionType)
    switch (actionType) {
      case 'pdf-results':
        return <Popup
          content={action.popupMessage}
          trigger={<CustomIcon
            style={{ cursor: 'pointer' }} name={action.name as SemanticICONS}
            onClick={() => handleDownloadPdfResults(row)}
            size='large'
          />}
        />
      case 'show':
        return <Popup
          content={action.popupMessage}
          trigger={<CustomIcon
            style={{ cursor: 'pointer' }}
            onClick={() => handleShow(row)}
            name={action.name as SemanticICONS}
            size='large'
          />}
        />
      default:
        return
    }
  }

  function renderTable() {
    return <TableComponent
      isFetchingData={isFetching}
      actions={actions}
      loadingAction={loadingAction}
      animated
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data) => setDataFiltered(data)}
      rows={rows}
      total={filtered}
    />
  }

  function renderPreviewModal() {
    return <PreviewModal
      id={previewModalId}
      handleClose={() => {
        setPreviewModalId(null)
        setUniqueId(null)
      }}
      uniqueId={uniqueId}
      farmerId={id}
    />
  }

  const { filtered, rows } = co2Footprints ? co2Footprints : { filtered: 0, rows: [] };

  return <Layout
    title={<span dangerouslySetInnerHTML={{ __html: t('CO2 footprint') }} />}
    childContainerSize={'300px'}
    fontSizeHeader={'18px'}
    useSpinner
    isBusy={isLoading}
  >
    {renderPreviewModal()}
    {renderTable()}
  </Layout>
}
export default ClimaFootprint